// ═══════════════════════════════════════════════════════════════
// CANVAS OVERLAYS — Marching ants, transform handles, brush cursor
// ═══════════════════════════════════════════════════════════════
// All functions accept s (scale = 1/zoom). Stroke widths, dashes,
// handle sizes, and fonts are multiplied by s so they stay constant
// in screen pixels. The overlay canvas is at document dimensions
// inside vpRef — CSS transform handles zoom/pan/rotation.

export function ants(ctx,sel,off,s){
  if(!sel||!sel.mask)return;
  ctx.save();
  const m=sel.mask,sw=ctx.canvas.width,sh=ctx.canvas.height;
  const y0=Math.max(0,sel.y),y1=Math.min(sh,sel.y+sel.h);
  const x0=Math.max(0,sel.x),x1=Math.min(sw,sel.x+sel.w);

  const drawEdges=()=>{
    for(let y=y0;y<=y1;y++){
      let inRun=false,runX=0;
      for(let x=x0;x<=x1;x++){
        const above=y>0&&y<=sh?m[(y-1)*sw+Math.min(x,sw-1)]>=128:false;
        const below=y<sh&&y>=0?m[y*sw+Math.min(x,sw-1)]>=128:false;
        const isEdge=(above!==below)&&x<x1;
        if(isEdge&&!inRun){inRun=true;runX=x;}
        else if(!isEdge&&inRun){ctx.moveTo(runX,y);ctx.lineTo(x,y);inRun=false;}
      }
      if(inRun){ctx.moveTo(runX,y);ctx.lineTo(x1,y);}
    }
    for(let x=x0;x<=x1;x++){
      let inRun=false,runY=0;
      for(let y=y0;y<=y1;y++){
        const left=x>0&&x<=sw?m[Math.min(y,sh-1)*sw+(x-1)]>=128:false;
        const right=x<sw&&x>=0?m[Math.min(y,sh-1)*sw+x]>=128:false;
        const isEdge=(left!==right)&&y<y1;
        if(isEdge&&!inRun){inRun=true;runY=y;}
        else if(!isEdge&&inRun){ctx.moveTo(x,runY);ctx.lineTo(x,y);inRun=false;}
      }
      if(inRun){ctx.moveTo(x,runY);ctx.lineTo(x,y1);}
    }
  };

  ctx.beginPath();drawEdges();
  ctx.strokeStyle="rgba(0,0,0,0.6)";ctx.lineWidth=0.8*s;ctx.setLineDash([]);ctx.stroke();
  ctx.strokeStyle="rgba(255,255,255,0.9)";ctx.lineWidth=0.8*s;ctx.setLineDash([4*s,4*s]);ctx.lineDashOffset=-off*0.3;ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

export function antsPreview(ctx,sd2,off,s){
  if(!sd2)return;
  ctx.save();
  const drawShape=()=>{
    if(sd2.t==="rect"){ctx.rect(sd2.x,sd2.y,sd2.w,sd2.h);}
    else if(sd2.t==="ellipse"){ctx.ellipse(sd2.x+sd2.w/2,sd2.y+sd2.h/2,Math.abs(sd2.w/2),Math.abs(sd2.h/2),0,0,Math.PI*2);}
    else if((sd2.t==="lasso"||sd2.t==="polyLasso")&&sd2.points&&sd2.points.length>1){
      ctx.moveTo(sd2.points[0].x,sd2.points[0].y);
      for(let i=1;i<sd2.points.length;i++)ctx.lineTo(sd2.points[i].x,sd2.points[i].y);
      if(sd2.t==="polyLasso")ctx.lineTo(sd2.points[0].x,sd2.points[0].y);
    }
  };
  ctx.beginPath();drawShape();
  ctx.strokeStyle="rgba(0,0,0,0.6)";ctx.lineWidth=0.8*s;ctx.setLineDash([]);ctx.stroke();
  ctx.strokeStyle="rgba(255,255,255,0.9)";ctx.lineWidth=0.8*s;ctx.setLineDash([4*s,4*s]);ctx.lineDashOffset=-off*0.3;ctx.stroke();
  if(sd2.t==="polyLasso"&&sd2.points&&sd2.points.length>0){
    ctx.setLineDash([]);ctx.fillStyle="rgba(96,165,250,0.9)";
    for(const pt of sd2.points){ctx.beginPath();ctx.arc(pt.x,pt.y,3*s,0,Math.PI*2);ctx.fill();}
  }
  ctx.setLineDash([]);
  ctx.restore();
}

export function tfmH(ctx,t,s){if(!t)return;const{x,y,w,h}=t,hs=6*s,rot=t.rot||0;
  const cx3=x+w/2,cy3=y+h/2;
  ctx.save();ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality="high";ctx.translate(cx3,cy3);ctx.rotate(rot*Math.PI/180);
  const skX=Math.tan((t.skewX||0)*Math.PI/180),skY=Math.tan((t.skewY||0)*Math.PI/180);
  if(skX||skY)ctx.transform(1,skY,skX,1,0,0);
  if(t.origData){
    // Cache the OffscreenCanvas to avoid re-creating every frame
    if(!t._cachedCanvas||t._cachedCanvas.width!==t.origData.width){
      t._cachedCanvas=new OffscreenCanvas(t.origData.width,t.origData.height);
      t._cachedCanvas.getContext("2d").putImageData(t.origData,0,0);
    }
    ctx.globalAlpha=0.85;
    ctx.drawImage(t._cachedCanvas,t.origX,t.origY,t.origW,t.origH,-w/2,-h/2,w,h);
    ctx.globalAlpha=1;
  }
  // Ghost outline of original bounds (before transform)
  if(t.origW!==w||t.origH!==h||t.origX!==x||t.origY!==y){
    ctx.save();ctx.rotate(-rot*Math.PI/180);ctx.translate(-cx3,-cy3);
    ctx.setLineDash([2*s,4*s]);ctx.strokeStyle="rgba(96,165,250,0.25)";ctx.lineWidth=0.5*s;
    ctx.strokeRect(t.origX,t.origY,t.origW,t.origH);ctx.setLineDash([]);
    ctx.restore();ctx.translate(0,0); // reset to rotated frame
  }
  // Bounding box
  ctx.setLineDash([4*s,3*s]);ctx.strokeStyle="rgba(96,165,250,0.7)";ctx.lineWidth=1*s;ctx.strokeRect(-w/2,-h/2,w,h);ctx.setLineDash([]);
  // Center crosshair
  ctx.strokeStyle="rgba(96,165,250,0.3)";ctx.lineWidth=0.5*s;
  ctx.beginPath();ctx.moveTo(-6*s,0);ctx.lineTo(6*s,0);ctx.moveTo(0,-6*s);ctx.lineTo(0,6*s);ctx.stroke();
  // Corner handles (squares)
  for(const[hx2,hy2]of[[-w/2,-h/2],[w/2,-h/2],[w/2,h/2],[-w/2,h/2]]){ctx.fillStyle="#1e1e1e";ctx.fillRect(hx2-hs/2,hy2-hs/2,hs,hs);ctx.strokeStyle="#60a5fa";ctx.lineWidth=1.5*s;ctx.strokeRect(hx2-hs/2,hy2-hs/2,hs,hs);}
  // Edge handles (circles)
  for(const[hx2,hy2]of[[0,-h/2],[w/2,0],[0,h/2],[-w/2,0]]){ctx.beginPath();ctx.arc(hx2,hy2,hs/2,0,Math.PI*2);ctx.fillStyle="#1e1e1e";ctx.fill();ctx.strokeStyle="#60a5fa";ctx.lineWidth=1.5*s;ctx.stroke();}
  // Rotation handle
  ctx.beginPath();ctx.moveTo(0,-h/2);ctx.lineTo(0,-h/2-22*s);ctx.strokeStyle="rgba(96,165,250,0.6)";ctx.lineWidth=1*s;ctx.stroke();
  ctx.beginPath();ctx.arc(0,-h/2-22*s,5*s,0,Math.PI*2);ctx.fillStyle="#2563eb";ctx.fill();ctx.strokeStyle="#60a5fa";ctx.lineWidth=1.5*s;ctx.stroke();
  ctx.restore();
  // Info label
  ctx.save();ctx.font=(10*s)+"px monospace";ctx.fillStyle="rgba(96,165,250,0.8)";ctx.textAlign="center";
  const info=`${Math.round(w)}\u00d7${Math.round(h)}${rot!==0?" "+Math.round(rot)+"\u00b0":""}${t.skewX?" skX:"+Math.round(t.skewX):""}${t.skewY?" skY:"+Math.round(t.skewY):""}`;
  ctx.fillText(info,cx3,cy3+h/2+18*s);
  // Modifier hints
  ctx.font=(8*s)+"px monospace";ctx.fillStyle="rgba(96,165,250,0.45)";
  ctx.fillText("Shift=lock  Alt=center",cx3,cy3+h/2+28*s);
  ctx.restore();
}

export function bCur(ctx,x,y,sz,s){ctx.save();const r=sz/2;ctx.strokeStyle="rgba(255,255,255,.7)";ctx.lineWidth=1*s;ctx.setLineDash([]);ctx.beginPath();ctx.arc(x,y,r,0,Math.PI*2);ctx.stroke();ctx.strokeStyle="rgba(0,0,0,.4)";ctx.setLineDash([3*s,3*s]);ctx.beginPath();ctx.arc(x,y,r,0,Math.PI*2);ctx.stroke();ctx.setLineDash([]);ctx.strokeStyle="rgba(255,255,255,.9)";ctx.lineWidth=1*s;const c=4*s;ctx.beginPath();ctx.moveTo(x-c,y);ctx.lineTo(x+c,y);ctx.moveTo(x,y-c);ctx.lineTo(x,y+c);ctx.stroke();ctx.restore();}
